import { isBoolean } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { KeyValue } from '@angular/common';
import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnIncidentsAPI, SnIntegrationsAPI } from '@lib/api/services';
import { FsFormatDatePipe } from '@lib/common/pipes';
import { GOOGLE_MAP_POPUP_DATA } from '@lib/geo/google/map';
import { SentryVaultComponent } from '@lib/vault';

import { IMediaInterface, SnLprRead, SnPlate } from '../../api';
import { PlatesService } from '../../services';
import { LprPlateImageDialogComponent } from '../lpr-plate-image-dialog/lpr-plate-image-dialog.component';

@Component({
  selector: 'fs-plate-popup',
  templateUrl: './plate-info-popup.component.html',
  styleUrls: ['./plate-info-popup.component.scss'],
})
export class PlateInfoPopupComponent implements OnDestroy, OnInit {
  public alarmIcon: string = null;
  public croppedImg: HTMLCanvasElement;

  public mediaCarousel: any[] = [];
  public activeMedia: IMediaInterface;

  private readonly _destroy$ = new Subject();

  constructor(
    @Inject(GOOGLE_MAP_POPUP_DATA) public readonly data: any,
    private readonly _dialog: MatDialog,
    private readonly _integrationsAPI: SnIntegrationsAPI,
    private readonly _incidentsAPI: SnIncidentsAPI,
    private readonly _cd: ChangeDetectorRef,
    private readonly _platesService: PlatesService,
  ) {
    this._platesService.fields$.pipe(takeUntil(this._destroy$)).subscribe((fields) => {
      this.data.fields = fields;
    });
    if (!data?.plate?.details) {
      data.plate = {
        details: {
          ...data.plate,
        },
      };
    }
  }

  public determineContextImage(): void {
    this.mediaCarousel = this.data.plate.details.media;
    this.mediaCarousel.sort((x, y) => {
      return x.type === 'context' ? -1 : y.type === 'context' ? 1 : 0;
    });
    this.activeMedia = this.mediaCarousel[0];
    this._cd.detectChanges();
  }

  public trackMedia(index: number, mediaFile: IMediaInterface): any {
    return mediaFile.type;
  }

  public setPlaceholderImage(event: any): void {
    event.target.src = '/assets/map-icons/No_Image_Found.png';
  }

  public selectMedia(media: IMediaInterface): void {
    this.activeMedia = media;
  }

  public nextImg(): void {
    if (this.mediaCarousel[1]) {
      this.activeMedia = this.mediaCarousel[0];
    }
  }

  public previousImg(): void {
    if (this.mediaCarousel[0]) {
      this.activeMedia = this.mediaCarousel[1];
    }
  }

  public exclude(key: string): boolean {
    return (
      [
        'media',
        'id',
        'geometry',
        'organizationId',
        'cameraId',
        'plateConfidence',
        'plateStateConfidence',
        'vehicleBodyTypeConfidence',
        'vehicleColorConfidence',
        'vehicleMakeConfidence',
        'vehicleModelConfidence',
        'vehicleYearConfidence',
      ].indexOf(key) > -1
    );
  }
  public ngOnInit(): void {
    this.determineCameraType();
    this.determineContextImage();
    this._cd.detectChanges();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public openEnlargedImageDialog(): void {
    this._dialog.open(LprPlateImageDialogComponent, {
      data: this.data,
      panelClass: 'lpr-plate__dialog-container',
    });
  }

  public isActive(media: IMediaInterface): boolean {
    return this.activeMedia && this.activeMedia.url === media.url;
  }

  public formatDataKeys(data: any[]): any[] {
    const formattedData: any = [];
    data.forEach((item: any) => {
      formattedData.push(
        Object.keys(item).reduce((accumulator, key) => {
          accumulator[key.toLowerCase()] = item[key];
          return accumulator;
        }, {}),
      );
    });
    return formattedData;
  }

  public isObject(item: any): boolean {
    return Object.prototype.toString.call(item).indexOf('Object') > -1;
  }

  public lprPage(data: SnLprRead): void {
    window.open(`/plates/${data.id}`);
  }

  public printLpr(): void {
    const content = document.getElementById('printContent').innerHTML;
    const popupWin = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
    popupWin.document.open();
    popupWin.document.write(`
      <html>
        <head>
          <title>LPR Plate Details</title>
        </head>
        <body onload="window.print();window.close()">${content}</body>
      </html>`);
    popupWin.document.close();
  }

  public isArrays(item: any): boolean {
    return Array.isArray(item);
  }

  public isString(item: any): boolean {
    return typeof item === 'string';
  }

  public isTimestamp(key: string): boolean {
    return key?.toString()?.toLowerCase()?.indexOf('timestamp') > -1;
  }

  public formatDate(data: Date | string): string {
    const date = new Date(data);
    return new FsFormatDatePipe().transform(date, 'medium');
  }

  public originalOrder = (): number => {
    return 0;
  };

  public excludeFlockData(key: string): boolean {
    return (
      [
        'box',
        'geometry',
        'imageUrl',
        'integratedLprType',
        'type',
        'eventTimestamp',
        'cameraId',
      ].indexOf(key) > -1
    );
  }

  public parseFlockData(item: KeyValue<string, any>): string {
    if (isBoolean(item.value)) {
      return item.value ? 'Yes' : 'No';
    }
    return !item.value ? 'N/A' : item.value;
  }

  public determineCameraType(): void {
    switch (this.data.plate.type) {
      case 'FS ALPR':
        this.alarmIcon = 'Icon_FS';
        break;
      case 'REKOR ALPR':
        this.alarmIcon = 'Icon_Rekor';
        break;
      case 'VIGILANT LEARN ALPR':
        this.alarmIcon = 'Icon_Vigilant';
        break;
      case 'DroneSense':
        this.alarmIcon = 'Icon_DroneSense';
        break;
      case 'Eagle Eye Networks ALPR':
        this.alarmIcon = 'Icon_een';
        break;
      case 'GENETEC ALPR':
      case 'GENETEC SHARPV ALPR':
      case 'GENETEC SHARP3 ALPR':
        this.alarmIcon = 'Icon_Genetec';
        break;
      default:
        this.alarmIcon = null;
        break;
    }
  }

  public sendToVault(plate: SnPlate): void {
    const dialog = this._dialog.open(SentryVaultComponent);
    dialog
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((vaultID) => {
        const vault = vaultID.id || vaultID;
        if (vault) {
          this._addToVault(vault, plate.id);
        }
      });
  }

  private _addToVault(vaultID: string, lprHitId: string): void {
    this._incidentsAPI.addLprToVault(vaultID, lprHitId).pipe(takeUntil(this._destroy$)).subscribe();
  }
}
